import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../../context/authContext";
import {
  getReferralCode,
  clearReferralCode,
  getLandingSlug,
  clearLandingSlug,
  getUtmParams,
} from "../../../../services/campaignAttribution";
import { trackLandingRegistration } from "../../../../services/landingTracking";
import {
  LEGAL_DOCUMENT_URLS,
  toPublicAssetHref,
} from "../../../../config/legalDocuments";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faChevronDown, faShieldAlt, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import {
  PIN_LENGTH,
  emptyPinArray,
  rememberPinLength,
  validateNewPin,
} from "../../../../services/api";
import { PinInput, PinLengthToggle } from "../../../../components/ui";
import { departamentos, ciudadesDe } from "../../../../data/colombiaDepartments";
import WelcomeBonusStrip from "../../../../components/bonus/WelcomeBonusStrip";
import styles from "./registroForm.module.css";

const hasLandingSlugNow = () => Boolean(getLandingSlug());

/** Códigos de referido: letras, números y guion (máx. 15, límite del backend). */
const REFERRAL_CODE_RE = /^[A-Z0-9-]{4,15}$/;

// Lista de países con código de marcación
const countries = [
  { code: "CO", name: "Colombia", dial: "+57", flag: "🇨🇴" },
  { code: "CR", name: "Costa Rica", dial: "+506", flag: "🇨🇷" },
  { code: "MX", name: "México", dial: "+52", flag: "🇲🇽" },
  { code: "US", name: "Estados Unidos", dial: "+1", flag: "🇺🇸" },
  { code: "ES", name: "España", dial: "+34", flag: "🇪🇸" },
  { code: "AR", name: "Argentina", dial: "+54", flag: "🇦🇷" },
  { code: "PE", name: "Perú", dial: "+51", flag: "🇵🇪" },
  { code: "CL", name: "Chile", dial: "+56", flag: "🇨🇱" },
  { code: "EC", name: "Ecuador", dial: "+593", flag: "🇪🇨" },
  { code: "VE", name: "Venezuela", dial: "+58", flag: "🇻🇪" },
  { code: "PA", name: "Panamá", dial: "+507", flag: "🇵🇦" },
];

export default function RegistroForm({ onSwitch, prefilled, returnTo }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, login, isLoading } = useAuth();
  const dropdownRef = useRef(null);
  const postAuthPath =
    returnTo || location.state?.returnTo || "/welcome";
  const initialPrefilled = prefilled || location.state?.prefilled || {};

  const [step, setStep] = useState(1); // 1 = datos, 2 = crear PIN
  const [referralCode] = useState(() => getReferralCode());
  // Con landing, el backend toma el código de la landing (landing_slug); sin landing,
  // el usuario puede escribir (o traer en el enlace) el código de un amigo.
  const [hasLanding] = useState(() => Boolean(getLandingSlug()));
  const [friendCode, setFriendCode] = useState(() =>
    hasLandingSlugNow() ? "" : getReferralCode().toUpperCase()
  );
  const [formData, setFormData] = useState({
    identification: initialPrefilled.identification || "",
    birth_date: "",
    first_name: "",
    last_name: "",
    email: initialPrefilled.email || "",
    phone_number: initialPrefilled.phone_number || "",
    departamento: "",
    ciudad: "",
  });
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  // El usuario elige PIN de 4 o 6 dígitos (6 por defecto: más seguro)
  const [pinLength, setPinLength] = useState(PIN_LENGTH);
  const [pin, setPin] = useState(() => emptyPinArray(PIN_LENGTH));
  const [confirmPin, setConfirmPin] = useState(() => emptyPinArray(PIN_LENGTH));
  const pinInputRef = useRef(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Cerrar dropdown al hacer clic afuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setFormData(prev => ({ ...prev, phone_number: value }));
  };

  // Al cambiar el departamento se resetea la ciudad (dropdowns dependientes),
  // igual que en la app móvil. Estos datos alimentan el código DANE de los
  // archivos regulatorios (lugar de la compra).
  const handleDepartamentoChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, departamento: value, ciudad: "" }));
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowCountryDropdown(false);
  };

  // Formatear número de teléfono para mostrar
  const formatPhoneDisplay = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length <= 4) return cleaned;
    if (cleaned.length <= 8) return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
  };

  // Validar paso 1
  const handleStep1Submit = (e) => {
    e.preventDefault();
    setError("");
    
    if (!formData.identification || !formData.birth_date || !formData.first_name ||
        !formData.last_name || !formData.email || !formData.phone_number ||
        !formData.departamento || !formData.ciudad) {
      setError("Por favor completa todos los campos");
      return;
    }
    
    if (formData.phone_number.length < 8) {
      setError("El número de teléfono debe tener al menos 8 dígitos");
      return;
    }
    
    if (!acceptTerms) {
      setError("Debes aceptar los términos y condiciones");
      return;
    }

    const friend = friendCode.trim();
    if (!hasLanding && friend && !REFERRAL_CODE_RE.test(friend)) {
      setError("El código de referido no es válido. Revísalo o déjalo vacío.");
      return;
    }
    
    setStep(2);
  };

  // Cambiar entre PIN de 4 y 6 dígitos: limpia ambos campos y enfoca el PIN
  const handlePinLengthChange = (length) => {
    setPinLength(length);
    setPin(emptyPinArray(length));
    setConfirmPin(emptyPinArray(length));
    setError("");
    pinInputRef.current?.focus();
  };

  // Enviar registro
  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    
    const pinString = pin.join("");
    const confirmPinString = confirmPin.join("");
    
    const pinError = validateNewPin(pinString, pinLength);
    if (pinError) {
      setError(pinError);
      return;
    }
    
    if (pinString !== confirmPinString) {
      setError("Los PIN no coinciden");
      setConfirmPin(emptyPinArray(pinLength));
      return;
    }
    
    const fullPhoneNumber = `${selectedCountry.dial}${formData.phone_number}`;
    
    const landingSlug = getLandingSlug();

    const userData = {
      ...formData,
      phone_number: fullPhoneNumber,
      pin: pinString,
      // El backend espera `referral_code` (antes se enviaba `referred_by`, que ignoraba).
      ...(!hasLanding && friendCode.trim() && { referral_code: friendCode.trim() }),
      ...(landingSlug && { landing_slug: landingSlug }),
      ...getUtmParams(),
    };
    
    const result = await register(userData);
    
    if (result.success) {
      rememberPinLength(pinLength);
      setSuccess("¡Cuenta creada exitosamente!");
      trackLandingRegistration({
        user_id: result.user?.id,
      });
      // Limpiar código de referido después de registro exitoso
      clearReferralCode();
      clearLandingSlug();
      // Auto-login después del registro
      setTimeout(async () => {
        const loginResult = await login(fullPhoneNumber, pinString);
        if (loginResult.success) {
          navigate(postAuthPath);
        } else {
          onSwitch(); // Ir a login si falla el auto-login
        }
      }, 1500);
    } else {
      setError(result.error);
    }
  };

  const handleBack = () => {
    setStep(1);
    setPin(emptyPinArray(pinLength));
    setConfirmPin(emptyPinArray(pinLength));
    setError("");
  };

  return (
    <div className={styles.card}>
      {step === 1 ? (
        // PASO 1: DATOS PERSONALES
        <form onSubmit={handleStep1Submit}>
          {/* Header con icono */}
          <div className={styles.header}>
            <div className={styles.iconWrapper}>
              <FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
            </div>
            <h2 className={styles.title}>Registro de Usuario</h2>
            <p className={styles.subtitle}>Únete a la plataforma premium Gold</p>
            {hasLanding && referralCode && (
              <div className={styles.referralBadge}>
                Código de referido: <strong>{referralCode}</strong>
              </div>
            )}
          </div>

          <WelcomeBonusStrip variant="compact" className={styles.welcomeStrip} />
          
          {/* Nombre y Apellido */}
          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label}>Nombre</label>
              <input 
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Ej. Juan"
                className={styles.input}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Apellido</label>
              <input 
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Ej. Pérez"
                className={styles.input}
              />
            </div>
          </div>

          {/* Email y Cédula */}
          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label}>Correo electrónico</label>
              <input 
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="juan@example.com"
                className={styles.input}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Cédula/ID</label>
              <input 
                name="identification"
                value={formData.identification}
                onChange={handleChange}
                placeholder="Número de identificación"
                className={styles.input}
              />
            </div>
          </div>

          {/* Teléfono y Fecha de nacimiento */}
          <div className={styles.row}>
            <div className={styles.field}>
              <div className={styles.phoneInputContainer}>
                <div className={styles.countrySelector} ref={dropdownRef}>
                  <span className={styles.countryLabel}>Country code</span>
                  <button
                    type="button"
                    className={styles.countrySelectorBtn}
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  >
                    <span className={styles.flag}>{selectedCountry.flag}</span>
                    <span className={styles.dialCode}>{selectedCountry.dial}</span>
                    <FontAwesomeIcon icon={faChevronDown} className={`${styles.chevron} ${showCountryDropdown ? styles.chevronUp : ''}`} />
                  </button>
                  
                  {showCountryDropdown && (
                    <div className={styles.countryDropdown}>
                      {countries.map((country) => (
                        <div
                          key={country.code}
                          className={`${styles.countryOption} ${country.code === selectedCountry.code ? styles.countryOptionActive : ''}`}
                          onClick={() => handleCountrySelect(country)}
                        >
                          <span className={styles.flag}>{country.flag}</span>
                          <span className={styles.countryName}>{country.name}</span>
                          <span className={styles.dialCode}>{country.dial}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className={styles.phoneFieldWrapper}>
                  <span className={styles.phoneLabel}>Phone number</span>
                  <input
                    type="tel"
                    value={formatPhoneDisplay(formData.phone_number)}
                    onChange={handlePhoneChange}
                    placeholder="Ingresar número"
                    className={styles.phoneInput}
                    maxLength={12}
                  />
                </div>
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Fecha de nacimiento</label>
              <input 
                type="date"
                name="birth_date"
                value={formData.birth_date}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
                className={styles.input}
              />
            </div>
          </div>

          {/* Departamento y ciudad (lugar de la compra, para archivos DANE) */}
          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label}>Departamento</label>
              <select
                name="departamento"
                value={formData.departamento}
                onChange={handleDepartamentoChange}
                className={styles.input}
              >
                <option value="">Selecciona…</option>
                {departamentos.map((dep) => (
                  <option key={dep} value={dep}>{dep}</option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Ciudad</label>
              <select
                name="ciudad"
                value={formData.ciudad}
                onChange={handleChange}
                disabled={!formData.departamento}
                className={styles.input}
              >
                <option value="">
                  {formData.departamento ? "Selecciona…" : "Elige departamento"}
                </option>
                {ciudadesDe(formData.departamento).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Código de un amigo (opcional) */}
          {!hasLanding && (
            <div className={`${styles.field} ${styles.referralField}`}>
              <label className={styles.label} htmlFor="referral_code">
                ¿Te invitó un amigo? Código de referido (opcional)
              </label>
              <input
                id="referral_code"
                name="referral_code"
                value={friendCode}
                onChange={(e) =>
                  setFriendCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 15))
                }
                placeholder="Ej. AB3K9XQZ"
                className={styles.input}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
            </div>
          )}

          {/* Términos y condiciones */}
          <div className={styles.checkbox}>
            <input 
              type="checkbox" 
              id="terms"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
            />
            <label htmlFor="terms">
              Acepto los{" "}
              <a
                href={toPublicAssetHref(LEGAL_DOCUMENT_URLS.terms)}
                className={styles.termsLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                términos y condiciones
              </a>{" "}
              de uso y la{" "}
              <a
                href={toPublicAssetHref(LEGAL_DOCUMENT_URLS.privacy)}
                className={styles.termsLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                política de privacidad
              </a>{" "}
              de Gold.
            </label>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <button type="submit" className={styles.primaryBtn} disabled={isLoading}>
            CREAR CUENTA
          </button>

          <p className={styles.switchText}>
            ¿Ya tienes cuenta?{" "}
            <span onClick={onSwitch} className={styles.link}>Inicia sesión</span>
          </p>

          {/* Footer de seguridad */}
          <div className={styles.securityFooter}>
            <span className={styles.securityItem}>
              <FontAwesomeIcon icon={faShieldAlt} className={styles.securityIcon} />
              SEGURO SSL
            </span>
            <span className={styles.securityItem}>
              <FontAwesomeIcon icon={faCheckCircle} className={styles.securityIcon} />
              PRIVACIDAD GOLD
            </span>
          </div>
        </form>
      ) : (
        // PASO 2: CREAR PIN
        <form onSubmit={handleRegister}>
          <button type="button" className={styles.backBtn} onClick={handleBack}>
            ← Volver
          </button>
          
          <div className={styles.header}>
            <div className={styles.iconWrapper}>
              <FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
            </div>
            <h2 className={styles.title}>Crea tu PIN</h2>
            <p className={styles.subtitle}>
              Este será tu código de acceso de {pinLength} dígitos
            </p>
          </div>

          <PinLengthToggle
            value={pinLength}
            onChange={handlePinLengthChange}
            disabled={isLoading}
            label="Longitud de tu PIN"
            hint="Elige 4 o 6 dígitos. 6 dígitos es más seguro."
          />
          
          <label className={styles.pinLabel}>PIN</label>
          <div className={styles.pinWrapper}>
            <PinInput
              ref={pinInputRef}
              length={pinLength}
              value={pin}
              onChange={setPin}
              disabled={isLoading}
              ariaLabel="PIN"
            />
          </div>
          
          <label className={styles.pinLabel}>Confirmar PIN</label>
          <div className={styles.pinWrapper}>
            <PinInput
              length={pinLength}
              value={confirmPin}
              onChange={setConfirmPin}
              disabled={isLoading}
              ariaLabel="Confirmar PIN"
            />
          </div>

          {error && <p className={styles.error}>{error}</p>}
          {success && <p className={styles.success}>{success}</p>}

          <button 
            type="submit" 
            className={styles.primaryBtn}
            disabled={isLoading || pin.some(d => d === "") || confirmPin.some(d => d === "")}
          >
            {isLoading ? "Creando cuenta..." : "CREAR CUENTA"}
          </button>
        </form>
      )}
    </div>
  );
}